import {
  AuthProvider,
  OAuthActionType,
  OAuthErrorResponse,
  OAuthErrorType,
  OAuthResponse,
  OAuthSuccessResponse,
  OAuthSuccessType,
} from '@fsp/types';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface OAuthCallbackParams {
  // Success params
  type?: OAuthSuccessType;
  provider?: AuthProvider;
  action?: OAuthActionType;
  token?: string;
  refreshToken?: string;
  returnUrl?: string;
  state?: string;
  code?: string;
  verificationToken?: string;
  verified?: string;

  // Error params
  error?: OAuthErrorType | string;
  message?: string;
}

export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [statusMessage, setStatusMessage] = useState('Completing sign in...');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        const isPopup = window.opener !== null;
        const params = extractCallbackParams();

        if (params.error) {
          const errorMessage = params.message || params.error || 'Authentication failed';
          await handleError(isPopup, params.action, errorMessage);
          return;
        }

        if (!params.provider || !params.action) {
          await handleError(isPopup, params.action, 'Missing required parameters');
          return;
        }

        switch (params.action) {
          case OAuthActionType.LINK:
            setStatusMessage('Linking account...');
            await handleLinkAction(isPopup, params);
            break;

          case OAuthActionType.LOGIN:
            setStatusMessage('Signing you in...');
            await handleAuthAction(isPopup, params);
            break;
          case OAuthActionType.SIGNUP:
            setStatusMessage('Completing account creation...');
            await handleAuthAction(isPopup, params);
            break;

          case OAuthActionType.VERIFY:
            setStatusMessage('Verifying your account...');
            await handleVerifyAction(isPopup, params);
            break;

          default:
            await handleError(isPopup, params.action, 'Unknown OAuth action');
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        await handleError(window.opener !== null, null, 'Failed to process authentication');
      }
    };

    processCallback();
  }, []); // Empty dependency array to run only once

  const extractCallbackParams = (): OAuthCallbackParams => {
    const params: OAuthCallbackParams = {};

    // Extract all possible parameters
    params.provider = searchParams.get('provider') as AuthProvider | undefined;
    params.action = searchParams.get('action') as OAuthActionType | undefined;
    params.token = searchParams.get('token') || undefined;
    params.refreshToken = searchParams.get('refreshToken') || undefined;
    params.returnUrl = searchParams.get('returnUrl') || undefined;
    params.state = searchParams.get('state') || undefined;
    params.verificationToken = searchParams.get('verificationToken') || undefined;
    params.verified = searchParams.get('verified') || undefined;
    params.error = searchParams.get('error') || undefined;
    params.message = searchParams.get('message') || undefined;
    params.code = searchParams.get('code') || undefined;

    return params;
  };

  const sendResponseToPopup = (response: OAuthResponse): void => {
    if (window.opener) {
      const openerOrigin = window.opener.location.origin;
      window.opener.postMessage(response, openerOrigin);
      setTimeout(() => window.close(), 1000);
    }
  };

  const getErrorRedirectPath = (action: OAuthActionType | null | undefined): string => {
    return action === OAuthActionType.SIGNUP ? '/join' : '/login';
  };

  const handleError = async (
    isPopup: boolean,
    action: OAuthActionType | null | undefined,
    message: string
  ): Promise<void> => {
    const errorResponse: OAuthErrorResponse = {
      success: false,
      message,
    };

    if (isPopup) {
      sendResponseToPopup(errorResponse);
    } else {
      const redirectPath = getErrorRedirectPath(action);
      navigate(`${redirectPath}?error=${encodeURIComponent(message)}`);
    }
  };

  const handleLinkAction = async (isPopup: boolean, params: OAuthCallbackParams): Promise<void> => {
    if (!isPopup) {
      await handleError(isPopup, params.action, 'Link action requires popup window');
      return;
    }

    if (!params.state || !params.provider || !params.code) {
      await handleError(isPopup, params.action, 'Missing OAuth parameters for linking');
      return;
    }

    const successResponse: OAuthSuccessResponse = {
      success: true,
      provider: params.provider,
      action: OAuthActionType.LINK,
      state: params.state,
      code: params.code || '',
    };

    sendResponseToPopup(successResponse);
  };

  const handleAuthAction = async (isPopup: boolean, params: OAuthCallbackParams): Promise<void> => {
    console.log('Handling auth action:', params);
    if (!params.token || !params.provider || !params.action) {
      await handleError(isPopup, params.action, 'Missing authentication parameters');
      return;
    }

    try {
      localStorage.setItem('token', params.token);
      if (params.refreshToken) {
        localStorage.setItem('refreshToken', params.refreshToken);
      }

      const returnUrl = params.returnUrl || '/dashboard';

      if (isPopup) {
        const successResponse: OAuthSuccessResponse = {
          success: true,
          provider: params.provider,
          action: params.action,
          token: params.token,
          refreshToken: params.refreshToken || '',
          returnUrl,
        };

        await login(params.token);
        sendResponseToPopup(successResponse);
      } else {
        // For non-popup flow, authenticate and redirect
        console.log('Logging in user...');
        await login(params.token);
        navigate(returnUrl);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      await handleError(isPopup, params.action, 'Failed to complete authentication');
    }
  };

  const handleVerifyAction = async (
    isPopup: boolean,
    params: OAuthCallbackParams
  ): Promise<void> => {
    if (!isPopup) {
      await handleError(isPopup, params.action, 'Verify action requires popup window');
      return;
    }

    if (!params.verificationToken || !params.provider) {
      await handleError(isPopup, params.action, 'Missing verification parameters');
      return;
    }

    const successResponse: OAuthSuccessResponse = {
      success: true,
      provider: params.provider,
      action: OAuthActionType.VERIFY,
      verificationToken: params.verificationToken,
    };

    sendResponseToPopup(successResponse);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{ bgcolor: 'background.default' }}
    >
      <CircularProgress size={48} />
      <Typography
        variant="body1"
        sx={{
          mt: 2,
          color: 'text.secondary',
          textAlign: 'center',
        }}
      >
        {statusMessage}
      </Typography>
    </Box>
  );
};
